import React, { useState } from 'react'
import { Modal, Button, Form, Spinner, Row, Col } from 'react-bootstrap'
import useCreateAppleIdConfig from 'hooks/use-create-apple-id-config'

interface CreateAppleIdModalProps {
  show: boolean
  onHide: () => void
  onCreated?: (appleIdConfigId: string) => void
}

const CreateAppleIdModal: React.FC<CreateAppleIdModalProps> = ({
  show,
  onHide,
  onCreated,
}) => {
  const [appleIdEmail, setAppleIdEmail] = useState('')
  const [forwardingNumber, setForwardingNumber] = useState('')
  const [iMessageEnabled, setIMessageEnabled] = useState(true)
  const [facetimeEnabled, setFacetimeEnabled] = useState(true)
  const [smsForwardingEnabled, setSmsForwardingEnabled] = useState(false)
  const [callForwardingEnabled, setCallForwardingEnabled] = useState(false)

  const createMutation = useCreateAppleIdConfig()

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    createMutation.mutate(
      {
        appleIdEmail,
        forwardingNumber: forwardingNumber || undefined,
        iMessageEnabled,
        facetimeEnabled,
        smsForwardingEnabled,
        callForwardingEnabled,
      },
      {
        onSuccess: (data) => {
          // Reset form
          setAppleIdEmail('')
          setForwardingNumber('')
          setIMessageEnabled(true)
          setFacetimeEnabled(true)
          setSmsForwardingEnabled(false)
          setCallForwardingEnabled(false)

          // Notify parent and close
          if (onCreated && data?.id) {
            onCreated(data.id)
          }
          onHide()
        },
      }
    )
  }

  const handleCancel = () => {
    // Reset form
    setAppleIdEmail('')
    setForwardingNumber('')
    setIMessageEnabled(true)
    setFacetimeEnabled(true)
    setSmsForwardingEnabled(false)
    setCallForwardingEnabled(false)
    onHide()
  }

  return (
    <Modal show={show} onHide={handleCancel} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create New Apple ID</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {/* Apple ID Email */}
          <Form.Group className="mb-3">
            <Form.Label>Apple ID Email *</Form.Label>
            <Form.Control
              type="email"
              placeholder="example@icloud.com"
              value={appleIdEmail}
              onChange={(e) => setAppleIdEmail(e.target.value)}
              required
            />
          </Form.Group>

          {/* Forwarding Number */}
          <Form.Group className="mb-3">
            <Form.Label>Forwarding Number</Form.Label>
            <Form.Control
              type="text"
              placeholder="+1234567890"
              value={forwardingNumber}
              onChange={(e) => setForwardingNumber(e.target.value)}
            />
            <Form.Text className="text-muted">
              Optional: Number to forward calls/SMS to
            </Form.Text>
          </Form.Group>

          {/* Services */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="iMessage Enabled"
                  checked={iMessageEnabled}
                  onChange={(e) => setIMessageEnabled(e.target.checked)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="FaceTime Enabled"
                  checked={facetimeEnabled}
                  onChange={(e) => setFacetimeEnabled(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>

          {/* Forwarding Settings */}
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="SMS Forwarding Enabled"
                  checked={smsForwardingEnabled}
                  onChange={(e) => setSmsForwardingEnabled(e.target.checked)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  label="Call Forwarding Enabled"
                  checked={callForwardingEnabled}
                  onChange={(e) => setCallForwardingEnabled(e.target.checked)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={createMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={createMutation.isLoading || !appleIdEmail}
          >
            {createMutation.isLoading ? (
              <>
                <Spinner animation="border" size="sm" /> Creating...
              </>
            ) : (
              'Create Apple ID'
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  )
}

export default CreateAppleIdModal
